import { Component, Input, Output, EventEmitter, OnInit, AfterViewChecked, OnDestroy, ViewChild, ElementRef, Renderer2, HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IntervalObservable } from 'rxjs/observable/IntervalObservable';
import 'rxjs/add/operator/takeWhile';

import { BufferGraphService } from 'app/buffer-graph/services/buffer-graph.service';
import { CommonDataService } from 'app/services/common-data.service';
import { GraphData } from 'app/buffer-graph/models/graph-data';
import { CompareItem } from 'app/buffer-graph/models/compare-item';
import { ChartData } from 'app/buffer-graph/models/chart-data';
import { Size } from 'app/shared/models/size';
import { BufferGraphChartComponent } from 'app/buffer-graph/components/buffer-graph-chart.component/buffer-graph-chart.component';
import { ResizeDirection } from 'app/resize-glyph/resize-glyph.component';
import { ITabInfo } from 'app/buffer-graph/models/tab-info-interface';
import { ItemOrderHistoryTabInfo } from 'app/buffer-graph/models/item-order-history/item-order-history-tab-info';
import { ItemOrderHistory } from 'app/buffer-graph/models/item-order-history/item-order-history';
import { ItemOrderHistoryFresh } from 'app/buffer-graph/models/item-order-history/item-order-history-fresh';
import { BufferChangeHistory } from 'app/buffer-graph/models/buffer-change-history/buffer-change-history';
import { BufferChangeHistoryTabInfo } from 'app/buffer-graph/models/buffer-change-history/buffer-change-history-tab-info';
import { ItemActionsTabInfo } from 'app/buffer-graph/models/item-actions/item-actions-tab-info';
import { ItemAction } from 'app/buffer-graph/models/item-actions/item-action';
import { FreshItemActionHeader } from 'app/buffer-graph/models/item-actions/fresh-item-action-header';
import { BufferChangeReasonsTabInfo } from 'app/buffer-graph/models/buffer-change-reasons/buffer-change-reasons-tab-info';
import { SuppliersSchedulesTabInfo } from 'app/buffer-graph/models/suppliers-schedules/suppliers-schedules-tab-info';
import { AssortmentStructureTabInfo } from 'app/buffer-graph/models/assortment-structure/assortment-structure-tab-info';

type History = ItemOrderHistory | ItemOrderHistoryFresh;
type Action = ItemAction | FreshItemActionHeader;

@Component({
    selector: 'buffer-graph',
    templateUrl: './buffer-graph.component.html',
    styleUrls: ['./buffer-graph.component.css']
})
export class BufferGraphComponent implements OnInit, AfterViewChecked, OnDestroy {
    constructor(private readonly translateService: TranslateService, private readonly graphService: BufferGraphService, private readonly commonData: CommonDataService, private readonly renderer: Renderer2) {
        this.showErrors = new EventEmitter<{ errors: string[], isOnInitiating: boolean }>();
        this.showLoadingImg = new EventEmitter<boolean>();
        this.loadApiError = new EventEmitter<void>();
        //this.close = new EventEmitter<void>();
        this.changeBuffer = new EventEmitter<void>();
        this.resizeValue = 0;
        this.chartsHeightCoeff = +localStorage['BgChartsHeightCoeff'] || 0.5;
        this.chartsWidthCoeff = +localStorage['BgChartsWidthCoeff'] || 0.5;
        this.tabHeaders = [];
        this.tabs = {};
        this.iohStatuses = {};
        this.iohFulfillment = {};
    }

    @ViewChild('mainChart')
    private mainChartComponent: BufferGraphChartComponent;

    @ViewChild('mainChart', { read: ElementRef })
    private mainChartRef: ElementRef;

    @ViewChild('compareChart')
    private compareChartComponent: BufferGraphChartComponent;

    @ViewChild('compareChart', { read: ElementRef })
    private compareChartRef: ElementRef;

    @ViewChild('tabsContainer')
    private tabsRef: ElementRef;

    @ViewChild('charts')
    private chartsRef: ElementRef;

    @Input()
    private itemId: number;

    //@Output()
    //private readonly close: EventEmitter<void>;

    @Input()
    private canDrawChart: boolean;

    @Output()
    private readonly showErrors: EventEmitter<{ errors: string[], isOnInitiating?: boolean }>;

    @Output()
    private readonly showLoadingImg: EventEmitter<boolean>;

    @Output()
    private loadApiError: EventEmitter<void>;//TODO: display error message on modal

    @Output()
    private readonly changeBuffer: EventEmitter<void>;

    private get verticalDirection(): ResizeDirection {
        return ResizeDirection.Vertical;
    }

    private get horizontalDirection(): ResizeDirection {
        return ResizeDirection.Horizontal;
    }

    private get itemOrderHistoryTabId(): string {
        return ItemOrderHistoryTabInfo.id;
    }

    private get bufferChangeHistoryTabId(): string {
        return BufferChangeHistoryTabInfo.id;
    }

    private get itemActionsTabId(): string {
        return ItemActionsTabInfo.id;
    }

    private get bufferChangeReasonsTabId(): string {
        return BufferChangeReasonsTabInfo.id;
    }

    private get suppliersSchedulesTabId(): string {
        return SuppliersSchedulesTabInfo.id;
    }

    private get assortmentStructureTabId(): string {
        return AssortmentStructureTabInfo.id;
    }

    private apiLoaded: boolean;

    private apiError: boolean;

    private destroyed: boolean;

    private daysCount: number;

    //private shown: boolean;

    //private showLoadingImg: boolean;

    //private temporaryHidden: boolean;

    private mainChartData: ChartData;

    private cwhItemId: number;

    private compareItems: CompareItem[];

    private compareChartData: ChartData;

    private resizeTimer: any;

    private resizeValue: number;

    private chartsHeightCoeff: number;

    private chartsWidthCoeff: number;

    private chartsTemplateRows: string;

    private showTargetLevel: boolean;

    private isTabsVisible: boolean;

    private bodyTemplateColumns: string;

    private readonly tabHeaders: ITabInfo[];

    private readonly tabs: { [header: string]: ITabInfo };

    private readonly iohStatuses: { [index: number]: string };

    private readonly iohFulfillment: { [index: number]: string };

    ngOnInit() {
        if (window['google'] && window['google'].charts)
            this.apiLoaded = true;
        else {
            const head = document['head'] || document['getElementsByTagName']('head')[0];
            const script = this.renderer.createElement('script') as HTMLScriptElement;
            script.src = 'https://www.gstatic.com/charts/loader.js';
            script.type = 'text/javascript';
            script.onload = () => {
                google.charts.load('current', { /*packages: ['corechart']*/ });
                google.charts.setOnLoadCallback(() => this.apiLoaded = true);
                this.renderer.removeChild(head, script);
            };
            script.onerror = () => {
                this.renderer.removeChild(head, script);
                this.apiError = true;
                this.loadApiError.emit();
            }
            this.renderer.appendChild(head, script);
        }

        this.translateService.get(['ArrivedInTime', 'Late', 'OrderNotFulfilled', 'InTransit', 'InTransitLate', 'OrderCanceled', 'NoData', 'OrderBlocked', 'ArrivedMore', 'ArrivedLess', 'ArrivedFullQuantity'])
            .subscribe(t => {
                this.iohStatuses[1] = t.ArrivedInTime;
                this.iohStatuses[2] = t.Late;
                this.iohStatuses[3] = t.OrderNotFulfilled;
                this.iohStatuses[4] = t.InTransit;
                this.iohStatuses[5] = t.InTransitLate;
                this.iohStatuses[6] = t.OrderCanceled;
                this.iohStatuses[7] = t.NoData;
                this.iohStatuses[8] = t.OrderBlocked;

                this.iohFulfillment[1] = t.ArrivedMore;
                this.iohFulfillment[2] = t.ArrivedLess;
                this.iohFulfillment[3] = t.ArrivedFullQuantity;
            });

        const onReceived = (data: GraphData): void => {
            //this.initiated = true;

            let wait = true;
            IntervalObservable.create(5).takeWhile(() => wait && !this.destroyed && !this.apiError).subscribe(() => {
                if (!this.canDrawChart && !this.apiLoaded)
                    return;

                wait = false;
                this.mainChartData = data.chartData;
            });
        };

        this.getGraphData(this.itemId, onReceived);

        this.graphService.getCompareData(this.itemId).subscribe(data => {
            if (data == null || data.accessDenied)
                return;

            this.cwhItemId = data.cwhItemId;
            this.compareItems = data.items;
        });
    }

    ngAfterViewChecked(): void {
        //if (this.shown)
        //    return;

        //this.showModal();
        //this.shown = true;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    //private showModal = (): void => {
    //    $('#bg-buffer-graph').on('shown.bs.modal',
    //        (e) => {
    //            $(e.target).off('shown.bs.modal');

    //            this.canDrawChart = true;
    //        }).on('hidden.bs.modal',
    //        (e) => {
    //            $(e.target).off('hidden.bs.modal');

    //            if (!this.temporaryHidden)
    //                this.close.emit();
    //            else
    //                this.temporaryHidden = false;
    //        }).modal({ backdrop: 'static', keyboard: false });
    //}

    //private hideModal = (): void => {
    //    $('#bg-buffer-graph').modal('hide');
    //}

    //private showErrors = (errors: string[], onOk?: () => void): void => {
    //    this.temporaryHidden = true;
    //    this.hideModal();
    //    this.commonData.showErrors(errors, onOk || this.showModal);
    //}

    private getGraphData = (itemId: number, onDataReceived: (data: GraphData) => void): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getBufferGraphData(itemId, this.daysCount).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetBufferGraphDataError')
                    .subscribe(error => this.showErrors.emit({ errors: [error], isOnInitiating: this.mainChartData == null }));
                return;
            }

            onDataReceived(data.graphData);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e], isOnInitiating: this.mainChartData == null });
            });
    }

    private resizeAndRedrawCharts = (): void => {
        this.mainChartComponent.resizeAndRedrawCharts();

        if (this.compareChartData)
            this.compareChartComponent.resizeAndRedrawCharts();
    }

    private setTabsVisibility = (visible: boolean): void => {
        this.isTabsVisible = visible;
        this.setChartsWidth();
        this.resizeAndRedrawCharts();
        //if (visible)
        //    setTimeout(() => this.graphService.notifyTabsResize(), 1);
    }

    private activateTab = (tabInfo?: ITabInfo): void => {
        if (tabInfo || this.tabHeaders.every(t => !t.isActive))
            this.tabHeaders.forEach((t, i) => t.isActive = t === tabInfo || (tabInfo == null && i === this.tabHeaders.length - 1));
    }

    private removeTab = (tabInfo: ITabInfo): void => {
        const idx = this.tabHeaders.indexOf(tabInfo);
        if (idx < 0)
            return;

        this.tabs[this.tabHeaders[idx].id] = null;
        this.tabHeaders.splice(idx, 1);
        if (this.tabHeaders.length === 0)
            this.setTabsVisibility(false);
        else
            this.activateTab();
    }

    private showItemOrderHistory = (): void => {
        this.loadItemOrderHistoryData(data => {
            const tab = new ItemOrderHistoryTabInfo({ isFresh: data.isFresh, sku: data.sku, warehouseCode: data.warehouseCode, orders: data.orders, canSend: data.canSend });
            this.tabHeaders.push(tab);
            this.tabs[this.itemOrderHistoryTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);
        });
    }

    private loadItemOrderHistoryData = (onLoaded: (data: { accessDenied: boolean, isFresh: boolean, sku: string, warehouseCode: string, orders: History[], canSend: boolean }) => void, daysCount?: number): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getItemOrderHistory(this.itemId, this.iohStatuses, this.iohFulfillment, daysCount).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetOrderHistoryError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

            onLoaded(data);
            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private showBufferChangeHistory = (): void => {
        this.loadBufferChangeHistoryData(data => {
            const tab = new BufferChangeHistoryTabInfo(this.translateService, { changes: data.changes, sku: data.sku, warehouseCode: data.warehouseCode, canSend: data.canSend });
            this.tabHeaders.push(tab);
            this.tabs[this.bufferChangeHistoryTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);
        });
    }

    private loadBufferChangeHistoryData = (onLoaded: (data: { accessDenied: boolean, changes: BufferChangeHistory[], sku: string, warehouseCode: string, canSend: boolean }) => void, daysCount?: number): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getBufferChangeHistory(this.itemId, daysCount).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetBufferChangeHistoryError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

            onLoaded(data);
            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private showItemActions = (): void => {
        this.loadItemActionsData(data => {
            const tab = new ItemActionsTabInfo({ actions: data.actions, isFresh: data.isFresh, sku: data.sku, warehouseCode: data.warehouseCode, canSend: data.canSend });
            this.tabHeaders.push(tab);
            this.tabs[this.itemActionsTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);
        });
    }

    private loadItemActionsData = (onLoaded: (data: { accessDenied: boolean, isFresh: boolean, actions: Action[], sku: string, warehouseCode: string, canSend: boolean }) => void, showAll?: boolean): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getItemActions(this.itemId, showAll).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetActionsError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

            onLoaded(data);
            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private showBufferChangeReasons = (): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getBufferChangeReasons(this.itemId, this.mainChartData.startDate, this.mainChartData.endDate).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetBufferChangeReasonsError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

            const tab = new BufferChangeReasonsTabInfo({ reasons: data.reasons, startDate: this.mainChartData.startDate, endDate: this.mainChartData.endDate });
            this.tabHeaders.push(tab);
            this.tabs[this.bufferChangeReasonsTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);

            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private showSuppliersSchedules = (): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getSuppliersSchedules(this.itemId).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetSuppliersSchedulesError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

            const tab = new SuppliersSchedulesTabInfo({ schedules: data.schedules });
            this.tabHeaders.push(tab);
            this.tabs[this.suppliersSchedulesTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);

            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private showAssortmentStructure = (): void => {
        this.showLoadingImg.emit(true);
        this.graphService.getAssortmentStructure(this.itemId).subscribe(data => {
            if (data == null || data.accessDenied) {
                this.showLoadingImg.emit(false);
                this.translateService.get(data && data.accessDenied ? 'AccessDenied' : 'GetAssortmentStructureError')
                    .subscribe(error => this.showErrors.emit({ errors: [error] }));
                return;
            }

                const tab = new AssortmentStructureTabInfo({ states: data.states, balanceStructure: data.balanceStructure });
            this.tabHeaders.push(tab);
            this.tabs[this.assortmentStructureTabId] = tab;
            this.activateTab(tab);
            if (this.tabHeaders.length === 1)
                this.setTabsVisibility(true);

            this.showLoadingImg.emit(false);
        },
            e => {
                this.showLoadingImg.emit(false);
                this.showErrors.emit({ errors: [e.message || e] });
            });
    }

    private onMainChartDrawed = (): void => {
        if (!this.compareChartData)
            this.showLoadingImg.emit(false);
    }

    private onCompareChartDrawed = (): void => {
        if (this.mainChartComponent)
            this.mainChartComponent.resizeAndRedrawCharts();

        this.showLoadingImg.emit(false);
    }

    private onShowAllChart = (): void => {
        if (this.daysCount === -1)
            return;

        this.daysCount = -1;
        this.getGraphData(this.itemId,
            mainData => {
                if (this.compareChartData)
                    this.getGraphData(this.compareChartData.itemInfo.itemId, compareData => this.compareChartData = compareData.chartData);

                this.mainChartData = mainData.chartData;
            });
    }

    private onShowCompareChart = (itemId: number): void => {
        this.getGraphData(itemId, data => {
            this.compareChartData = data.chartData;

            this.setChartsHeight();

            this.mainChartComponent.resizeAndRedrawCharts();
        });
    }

    private onCloseCompareChart = (): void => {
        this.compareChartData = null;


        this.setChartsHeight();

        this.mainChartComponent.resizeAndRedrawCharts();
    }

    private setChartsHeight = (): void => {
        const coeff = this.compareChartData ? this.chartsHeightCoeff : 1;
        this.chartsTemplateRows = `${1 - coeff}fr min-content ${coeff}fr`;
    }

    private setChartsWidth = (): void => {
        const coeff = this.isTabsVisible ? this.chartsWidthCoeff : 1;
        this.bodyTemplateColumns = `${1 - coeff}fr min-content ${coeff}fr`;
    }

    private onChartsResizedBy = (size: Size): void => {
        if (this.resizeTimer)
            clearTimeout(this.resizeTimer);

        this.resizeValue += size.height;

        this.resizeTimer = setTimeout(() => {
            this.resizeTimer = null;

            const mainHeight = this.mainChartRef.nativeElement.offsetHeight;
            const compareHeight = this.compareChartRef.nativeElement.offsetHeight;

            this.chartsHeightCoeff = (mainHeight - this.resizeValue) / (mainHeight + compareHeight);
            if (this.chartsHeightCoeff < 0)
                this.chartsHeightCoeff = 0;
            else if (this.chartsHeightCoeff > 1)
                this.chartsHeightCoeff = 1;

            localStorage['BgChartsHeightCoeff'] = this.chartsHeightCoeff;
            this.setChartsHeight();

            this.mainChartComponent.resizeAndRedrawCharts();

            if (this.compareChartData)
                this.compareChartComponent.resizeAndRedrawCharts();

            this.resizeValue = 0;
        },
            100);
    }

    @HostListener('document:keydown', ['$event'])
    private onModalKeyDown = (event: KeyboardEvent): void => {
        if (!event.ctrlKey || event.altKey || event.shiftKey)
            return;

        let handled = false;
        switch (event.code) {
            case 'KeyT':
            case 'KeyL'://Target level
                this.showTargetLevel = !this.showTargetLevel;
                handled = true;
                break;
            case 'KeyI':
            case 'KeyO'://Item order history
                const iohTab = this.tabs[this.itemOrderHistoryTabId];
                if (iohTab)
                    this.removeTab(iohTab);
                else
                    this.showItemOrderHistory();
                handled = true;
                break;
            case 'KeyH'://Buffer change history
                const bchTab = this.tabs[this.bufferChangeHistoryTabId];
                if (bchTab)
                    this.removeTab(bchTab);
                else
                    this.showBufferChangeHistory();
                handled = true;
                break;
            case 'KeyA'://item actions
                const iaTab = this.tabs[this.itemActionsTabId];
                if (iaTab)
                    this.removeTab(iaTab);
                else
                    this.showItemActions();
                handled = true;
                break;
            case 'KeyR'://Buffer chage reasons
                const bcrTab = this.tabs[this.bufferChangeReasonsTabId];
                if (bcrTab)
                    this.removeTab(bcrTab);
                else
                    this.showBufferChangeReasons();
                handled = true;
                break;
            case 'KeyS'://Supplier schedule
                const ssTab = this.tabs[this.suppliersSchedulesTabId];
                if (ssTab)
                    this.removeTab(ssTab);
                else
                    this.showSuppliersSchedules();
                handled = true;
                break;
            case 'KeyG'://Assortment structure
                const asTab = this.tabs[this.assortmentStructureTabId];
                if (asTab)
                    this.removeTab(asTab);
                else
                    this.showAssortmentStructure();
                handled = true;
                break;
        }

        if (!handled)
            return;

        event.preventDefault();
        event.stopPropagation();

    }

    private onTabsResizedBy = (size: Size): void => {
        if (this.resizeTimer)
            clearTimeout(this.resizeTimer);

        this.resizeValue += size.width;

        this.resizeTimer = setTimeout(() => {
            this.resizeTimer = null;

            const chartsWidth = this.chartsRef.nativeElement.offsetWidth;
            const tabsWidth = this.tabsRef.nativeElement.offsetWidth;

            this.chartsWidthCoeff = (chartsWidth - this.resizeValue) / (chartsWidth + tabsWidth);
            if (this.chartsWidthCoeff < 0)
                this.chartsWidthCoeff = 0;
            else if (this.chartsWidthCoeff > 1)
                this.chartsWidthCoeff = 1;

            localStorage['BgChartsWidthCoeff'] = this.chartsWidthCoeff;
            this.setChartsWidth();

            this.resizeAndRedrawCharts();
            this.graphService.notifyTabsResize();

            this.resizeValue = 0;
        },
            100);
    }

    private onShowAllItemOrderHistory = (): void => {
        this.loadItemOrderHistoryData(data => {
            const tab = (this.tabs[this.itemOrderHistoryTabId] as ItemOrderHistoryTabInfo);
            tab.data.isAll = true;
            tab.data.orders = data.orders;
        },
            -1);
    }

    private onShowAllBufferChangeHistory = (): void => {
        this.loadBufferChangeHistoryData(data => {
            const tab = (this.tabs[this.bufferChangeHistoryTabId] as BufferChangeHistoryTabInfo);
            tab.data.isAll = true;
            tab.data.changes = data.changes;
        },
            -1);
    }

    private onShowItemActions = (showAll: boolean): void => {
        this.loadItemActionsData(data => {
            const tab = (this.tabs[this.itemActionsTabId] as ItemActionsTabInfo);
            tab.data.isAll = showAll;
            tab.data.actions = data.actions;
        },
            showAll);
    }

    private onChangeBufferClick = (): void => {
        this.changeBuffer.emit();
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/components/buffer-graph.component/buffer-graph.component.ts